"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GUS_SWEEP_MAX_PASSES = exports.GUS_ACCEPTABLE_FIELDS = void 0;
exports.checkEntityInGus = checkEntityInGus;
exports.acceptGusFields = acceptGusFields;
exports.runGusSweepBatch = runGusSweepBatch;
exports.fetchGusReport = fetchGusReport;
exports.runGusSweepLoop = runGusSweepLoop;
/**
 * GUS-4b — wołanie tras GUS z ekranu. Wzorzec wstrzykiwanego `fetcher` przepisany
 * z ./Modals/gusLookupService.ts, żeby test nie potrzebował sieci.
 *
 * Backend (PS-nodeJS, src/entities/EntitiesRouters.ts):
 *   POST entities/:id/gus/check            — pyta rejestr, zapisuje SAM werdykt
 *   POST entities/:id/gus/accept {fields}  — przepisuje z migawki wskazane pola
 *   POST entities/gus/sweep {limit}        — jedna partia przebiegu (bramka panelu)
 *   GET  entities/gus/report               — pięć list do sprzątania (bramka panelu)
 */
const MainSetupReact_1 = __importDefault(require("../React/MainSetupReact"));
exports.GUS_ACCEPTABLE_FIELDS = ["name", "address", "regon", "krs"];
async function callServer(path, init, fetcher) {
    const response = await fetcher(`${MainSetupReact_1.default.serverUrl}${path}`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        ...init,
    });
    if (!response.ok) {
        const msg = await response
            .json()
            .then((body) => body?.error)
            .catch(() => undefined);
        throw new Error(msg || `Błąd wywołania GUS: ${response.status}`);
    }
    return response.json();
}
async function checkEntityInGus(entityId, fetcher = fetch) {
    return callServer(`entities/${entityId}/gus/check`, { method: "POST" }, fetcher);
}
async function acceptGusFields(entityId, fields, fetcher = fetch) {
    return callServer(`entities/${entityId}/gus/accept`, { method: "POST", body: JSON.stringify({ fields }) }, fetcher);
}
async function runGusSweepBatch(limit, fetcher = fetch) {
    return callServer("entities/gus/sweep", { method: "POST", body: JSON.stringify(limit ? { limit } : {}) }, fetcher);
}
async function fetchGusReport(fetcher = fetch) {
    return callServer("entities/gus/report", { method: "GET" }, fetcher);
}
/**
 * Zapora na pętlę przebiegu. Serwer ma własną (100 partii w runGusSweepUntilDone),
 * ale ekran woła trasę partia po partii, więc musi mieć swoją — inaczej błąd
 * w warunku kolejki po stronie serwera zamienia otwartą kartę w nieskończone
 * odpytywanie rejestru GUS (limit jest wspólny z FIDmanem, pułapka P-4).
 */
exports.GUS_SWEEP_MAX_PASSES = 100;
/**
 * Pętla po `remaining`: woła partie, aż kolejka się wyczerpie.
 *
 * Przerywa nie tylko na `remaining === 0`, ale też gdy partia nie ruszyła ani jednego
 * podmiotu (`checked === 0`) i gdy serwer sam zgłosi przerwanie — bez tego rekord,
 * którego z jakiegoś powodu nie da się odhaczyć, kręciłby pętlę w kółko.
 */
async function runGusSweepLoop(onProgress, runBatch = (limit) => runGusSweepBatch(limit)) {
    const byStatus = {};
    let passes = 0;
    let checked = 0;
    let remaining = 0;
    let total = 0;
    while (passes < exports.GUS_SWEEP_MAX_PASSES) {
        const batch = await runBatch();
        passes += 1;
        checked += batch.checked;
        remaining = batch.remaining;
        if (passes === 1)
            total = batch.checked + batch.remaining;
        for (const [status, count] of Object.entries(batch.byStatus ?? {}))
            byStatus[status] = (byStatus[status] ?? 0) + count;
        onProgress({ passes, checked, remaining, total });
        if (batch.aborted)
            return { passes, checked, remaining, total, byStatus, aborted: true, abortReason: batch.abortReason };
        if (batch.remaining === 0 || batch.checked === 0)
            break;
    }
    const hitTheGuard = remaining > 0 && passes >= exports.GUS_SWEEP_MAX_PASSES;
    return {
        passes,
        checked,
        remaining,
        total,
        byStatus,
        aborted: hitTheGuard,
        abortReason: hitTheGuard
            ? `Przerwano po ${exports.GUS_SWEEP_MAX_PASSES} partiach, w kolejce zostało ${remaining} podmiotów`
            : undefined,
    };
}
